import { z } from "zod";
/**
 * «Роботы» (Паша 02.10.2026): the owner's read-only view of the Academy's robots — timers, watchers, table jobs and
 * agent tools around amoCRM. The registry lives outside Vekha (/etc/hypno-robots/robots.yaml); a root timer writes
 * its snapshot with live state every five minutes (`hypno-robot status --write`, schema 1), Vekha only reads and shows
 * it. Changing a robot goes through an agent (`hypno-robot off|on <id> --reason …`), never through this page.
 */
export const ROBOT_STATES = ["on", "off", "failed", "event", "on_demand", "unknown"];
export const RobotStateSchema = z.enum(ROBOT_STATES);
export const ROBOT_AMO_KINDS = ["pipeline", "stage", "field", "user", "tag", "task_type", "text"];
export const RobotAmoKindSchema = z.enum(ROBOT_AMO_KINDS);
/**
 * Whether a robot needs AI (Паша 02.10.2026: «если аккаунты ИИ откажут — всё сломается?»): none — no model at all;
 * optional — works without it, worse (Jev labels messages, rules decide without it); required — stops without it
 * (a model through OpenClaw, an agent's turn). The registry's `ai`; null — the card does not say.
 */
export const ROBOT_AI_LEVELS = ["none", "optional", "required"];
export const RobotAiSchema = z.enum(ROBOT_AI_LEVELS);
const Text = z.string();
const Moment = z.string().nullable();
/** What a robot relies on in amoCRM: a pipeline, a stage, a field, a user, a tag, a task type or a fixed text. */
export const RobotAmoRefSchema = z.object({
    kind: RobotAmoKindSchema,
    id: z.union([z.number(), z.string()]).nullable(),
    name: Text.nullable(),
    /** The pipeline of a stage, as the registry names it. */
    pipeline: Text.nullable(),
}).strict();
export const RobotLiveSchema = z.object({
    state: RobotStateSchema,
    stateSince: Moment,
    lastRunAt: Moment,
    lastResult: Text.nullable(),
    lastError: Text.nullable(),
    nextRunAt: Moment,
    units: z.array(z.object({
        unit: Text, active: Text.nullable(), enabled: Text.nullable(), result: Text.nullable(),
    }).strict()),
    /** The host watcher's problems of this robot: since when, or at least when the watcher last saw it. */
    problems: z.array(z.object({ key: Text.nullable(), text: Text, since: Moment, checkedAt: Moment }).strict()),
    /** A config flag the robot obeys: where it is, its value now, why it could not be read. */
    flag: z.object({ target: Text.nullable(), value: Text.nullable(), error: Text.nullable() }).strict().nullable(),
    /** «за 7 дней: …» — the robot's own week numbers, labels as the registry words them. */
    week: z.array(z.object({ label: Text, value: z.union([z.number(), Text]) }).strict()),
    switched: z.object({
        action: Text.nullable(), at: Moment, by: Text.nullable(), reason: Text.nullable(),
    }).strict().nullable(),
    note: Text.nullable(),
}).strict();
export const RobotCardSchema = z.object({
    id: Text.min(1),
    name: Text.min(1),
    group: Text,
    does: Text,
    when: Text.nullable(),
    amoWrites: z.array(Text),
    amoDepends: z.array(RobotAmoRefSchema),
    messages: z.array(Text),
    /** How the robot is switched: timer / flag / cron / code / none, what exactly, a hint for agents, the default. */
    switch: z.object({ kind: Text.nullable(), target: Text.nullable(), how: Text.nullable(), default: Text.nullable() }).strict().nullable(),
    metrics: Text.nullable(),
    owner: Text.nullable(),
    ifOff: Text.nullable(),
    offReason: Text.nullable(),
    /** Since when the registry says the robot is off (when live has no time of its own). */
    offSince: Moment,
    code: z.array(Text),
    notes: Text.nullable(),
    ai: RobotAiSchema.nullable(),
    /** What calls AI and what keeps working without it, in the registry's words. */
    aiNote: Text.nullable(),
    live: RobotLiveSchema,
    /** What of the card did not pass the check: those fields are shown empty, the rest of the card stays. */
    issues: z.array(Text),
}).strict();
/** A card the file had but Vekha could not read at all (no id or name, not an object). */
export const RobotRejectedSchema = z.object({ index: z.number().int().nonnegative(), id: Text.nullable(), reason: Text }).strict();
export const ROBOT_WARNING_KINDS = ["failed", "off_long", "status_stale", "status_missing", "status_unreadable", "status_invalid"];
export const RobotWarningSchema = z.object({
    kind: z.enum(ROBOT_WARNING_KINDS),
    robotId: Text.nullable(),
    robotName: Text.nullable(),
    /** Since when: the error, the switch-off, the last snapshot. */
    since: Moment,
    /** The error text or why the robot was switched off, when known. */
    reason: Text.nullable(),
}).strict();
/** One amoCRM thing and the robots relying on it: what to check before renaming or deleting it. */
export const RobotAmoDependencySchema = z.object({
    key: Text,
    kind: RobotAmoKindSchema,
    id: z.union([z.number(), z.string()]).nullable(),
    name: Text.nullable(),
    pipeline: Text.nullable(),
    /** Other spellings of the same thing in different cards. */
    otherNames: z.array(Text),
    robots: z.array(z.object({ id: Text, name: Text }).strict()),
}).strict();
/** «Без ИИ продолжат работать N из M; отключатся: …» — by the cards' `ai`. */
export const RobotsAiSummarySchema = z.object({
    /** Robots with `ai: none` or `optional`. */
    keepWorking: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    /** Robots with `ai: required`, in the order of the file. */
    stopping: z.array(z.object({ id: Text, name: Text }).strict()),
    /** Cards that do not say whether they need AI. */
    unknown: z.number().int().nonnegative(),
}).strict();
export const ROBOTS_FILE_STATES = ["ok", "stale", "missing", "unreadable", "invalid"];
export const RobotsOverviewSchema = z.object({
    /** ok — fresh snapshot; stale — older than 15 minutes; missing / unreadable / invalid — nothing to trust. */
    file: z.enum(ROBOTS_FILE_STATES),
    generatedAt: Moment,
    /** When Vekha read the file. */
    checkedAt: Text,
    registry: z.object({ path: Text.nullable(), commit: Text.nullable(), committedAt: Moment }).strict().nullable(),
    /** The host watcher behind `problems`: when it last checked, whether its answer was there at all. */
    hostWatch: z.object({ checkedAt: Moment, available: z.boolean().nullable() }).strict().nullable(),
    /** Group order of the file, then groups only robots name. */
    groups: z.array(Text),
    robots: z.array(RobotCardSchema),
    rejected: z.array(RobotRejectedSchema),
    /** What did not pass the check in the file itself (not in a card). */
    fileIssues: z.array(Text),
    warnings: z.array(RobotWarningSchema),
    dependencies: z.array(RobotAmoDependencySchema),
    ai: RobotsAiSummarySchema,
}).strict();
//# sourceMappingURL=robots.js.map